import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Save, ShieldCheck, History, Upload, FileText } from 'lucide-react';
import DocumentUploader from '../components/DocumentUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProcurementPolicy() {
  const navigate = useNavigate();
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [form, setForm] = useState({
    resolution_number: '', resolution_date: '', effective_date: '',
    quick_quotation_limit: 50000, minimum_quotations_required: 3,
    approval_authority: 'Secretary + Chairman',
    tender_threshold: 50000, notes: '',
  });
  const [resolutionDoc, setResolutionDoc] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [a, h] = await Promise.allSettled([
        axios.get(`${API}/buyer/policy/active`, { withCredentials: true }),
        axios.get(`${API}/buyer/policy/history`, { withCredentials: true }),
      ]);
      if (a.status === 'fulfilled') setActive(a.value.data);
      if (h.status === 'fulfilled') setHistory(h.value.data || []);
    } catch (e) { toast.error('Failed to load policy'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.resolution_number || !form.resolution_date || !form.effective_date) {
      return toast.error('Resolution number and dates are required');
    }
    setSaving(true);
    try {
      await axios.post(`${API}/buyer/policy`, {
        ...form,
        resolution_date: new Date(form.resolution_date).toISOString(),
        effective_date: new Date(form.effective_date).toISOString(),
        quick_quotation_limit: parseFloat(form.quick_quotation_limit) || 0,
        minimum_quotations_required: parseInt(form.minimum_quotations_required, 10) || 0,
        tender_threshold: parseFloat(form.tender_threshold) || 0,
        supporting_resolution: resolutionDoc[0] || null,
      }, { withCredentials: true });
      toast.success('Procurement policy saved as new version');
      setShowForm(false);
      setResolutionDoc([]);
      await load();
    } catch (er) { toast.error(er.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const formatINR = (v) => v == null ? '—' : `₹${Number(v).toLocaleString('en-IN')}`;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-navy text-white">
        <nav className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <button onClick={() => navigate('/buyer-dashboard')} className="text-white/70 hover:text-white inline-flex items-center" data-testid="policy-back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </button>
          <div className="font-bold flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-gold" /> Procurement Policy</div>
          <div />
        </nav>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 max-w-4xl">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-navy">Delegated Authority Register</h1>
          <p className="text-sm text-muted-foreground mt-1">One organization-wide policy. Version-controlled. Every procurement record automatically links to the active version.</p>
        </div>

        {loading ? (
          <div className="p-6 text-center text-muted-foreground">Loading…</div>
        ) : !active && !showForm ? (
          <div className="bg-card border-2 border-dashed border-border rounded-lg p-8 text-center" data-testid="policy-empty-state">
            <ShieldCheck className="h-10 w-10 text-orange mx-auto mb-3" />
            <div className="font-bold text-navy mb-1">No Procurement Policy Yet</div>
            <p className="text-xs text-muted-foreground mb-4">Pass a one-time committee resolution defining your Society's delegated authority — Quick Quotation Limit, Minimum Quotations, Approval Authority and Tender Threshold.</p>
            <button onClick={() => setShowForm(true)} className="px-5 py-2 rounded bg-orange text-white text-sm font-bold inline-flex items-center" data-testid="policy-create-btn">
              <Upload className="h-4 w-4 mr-2" /> Create Procurement Policy
            </button>
          </div>
        ) : null}

        {active && !showForm && (
          <div className="bg-card border border-border rounded-lg p-5 mb-6" data-testid="policy-active-card">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <div className="font-bold text-navy text-lg">Active Policy · v{active.version}</div>
                <div className="text-xs text-muted-foreground">Resolution {active.resolution_number} · effective {formatDate(active.effective_date)}</div>
              </div>
              <button onClick={() => setShowForm(true)} className="px-3 py-1.5 text-xs font-bold rounded bg-navy text-white" data-testid="policy-revise-btn">
                Revise Policy
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <Field label="Quick Quotation Limit" value={formatINR(active.quick_quotation_limit)} />
              <Field label="Minimum Quotations" value={active.minimum_quotations_required} />
              <Field label="Approval Authority" value={active.approval_authority} />
              <Field label="Tender Threshold (full tender required above)" value={formatINR(active.tender_threshold)} />
              <Field label="Resolution Date" value={formatDate(active.resolution_date)} />
              <Field label="Effective Date" value={formatDate(active.effective_date)} />
            </div>
            {active.supporting_resolution && (
              <div className="mt-3 text-xs text-green-700 inline-flex items-center gap-1">
                <FileText className="h-3 w-3" /> Supporting resolution on file: {active.supporting_resolution.filename}
              </div>
            )}
            {active.notes && <div className="mt-3 text-xs text-muted-foreground italic">{active.notes}</div>}
          </div>
        )}

        {showForm && (
          <form onSubmit={submit} className="bg-card border border-border rounded-lg p-5 mb-6 space-y-4" data-testid="policy-form">
            <h2 className="font-bold text-navy">{active ? `Revise Policy (creates v${(active.version || 0) + 1})` : 'New Procurement Policy'}</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input label="Resolution Number *" value={form.resolution_number} onChange={v => setForm({ ...form, resolution_number: v })} testid="policy-resolution-number" />
              <Input label="Approval Authority *" value={form.approval_authority} onChange={v => setForm({ ...form, approval_authority: v })} placeholder="e.g. Secretary + Chairman" testid="policy-approval-authority" />
              <Input label="Resolution Date *" type="date" value={form.resolution_date} onChange={v => setForm({ ...form, resolution_date: v })} testid="policy-resolution-date" />
              <Input label="Effective Date *" type="date" value={form.effective_date} onChange={v => setForm({ ...form, effective_date: v })} testid="policy-effective-date" />
              <Input label="Quick Quotation Limit (₹) *" type="number" value={form.quick_quotation_limit} onChange={v => setForm({ ...form, quick_quotation_limit: v })} testid="policy-quick-limit" />
              <Input label="Minimum Quotations Required *" type="number" value={form.minimum_quotations_required} onChange={v => setForm({ ...form, minimum_quotations_required: v })} testid="policy-min-quotations" />
              <Input label="Tender Threshold — Full Tender Required Above (₹) *" type="number" value={form.tender_threshold} onChange={v => setForm({ ...form, tender_threshold: v })} testid="policy-tender-threshold" />
              <Input label="Notes" value={form.notes} onChange={v => setForm({ ...form, notes: v })} testid="policy-notes" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-navy mb-1">Supporting Resolution Document</label>
              <DocumentUploader files={resolutionDoc} onChange={setResolutionDoc} purpose="procurement_policy" maxFiles={1} />
            </div>
            <div className="flex gap-2">
              <button type="submit" disabled={saving} className="px-5 py-2 rounded bg-orange text-white text-sm font-bold disabled:opacity-40 inline-flex items-center" data-testid="policy-save-btn">
                <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving…' : 'Save Policy'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2 rounded border border-input text-sm">Cancel</button>
            </div>
          </form>
        )}

        {history.length > 1 && (
          <div className="bg-card border border-border rounded-lg p-5" data-testid="policy-history-section">
            <div className="font-bold text-navy mb-3 inline-flex items-center gap-2"><History className="h-4 w-4" /> Revision History</div>
            <div className="space-y-2">
              {history.filter(p => !p.is_active).map(p => (
                <div key={p.id} className="p-3 rounded border border-border text-xs" data-testid={`policy-history-${p.version}`}>
                  <div className="flex items-center justify-between flex-wrap gap-1">
                    <div className="font-semibold text-navy">v{p.version} · {p.resolution_number}</div>
                    <div className="text-muted-foreground">{formatDate(p.effective_date)}</div>
                  </div>
                  <div className="text-muted-foreground mt-1">Quick limit {formatINR(p.quick_quotation_limit)} · Min quotes {p.minimum_quotations_required} · Threshold {formatINR(p.tender_threshold)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">{label}</div>
    <div className="text-sm font-semibold text-navy">{value || '—'}</div>
  </div>
);

const Input = ({ label, value, onChange, type = 'text', testid, placeholder }) => (
  <div>
    <label className="block text-xs font-semibold text-navy mb-1">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full px-3 py-2 rounded border border-input bg-background text-sm"
      data-testid={testid} />
  </div>
);
